import React, { MouseEvent, useCallback, useEffect, useMemo, useRef } from "react";

import { faReply } from "@fortawesome/pro-solid-svg-icons/faReply";
import { faReplyAll } from "@fortawesome/pro-solid-svg-icons/faReplyAll";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { isToday } from "date-fns";
import { formatDistanceToNow } from "date-fns";
import { uniqBy } from "lodash-es";
import { useIntl } from "react-intl";

import Email from "@mapmycustomers/shared/types/email/Email";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import { injectLocale } from "@app/util/dates";
import { formatDate } from "@app/util/formatters";

import EmailAttachmentsIcon from "../components/EmailAttachmentsIcon";
import EmailHtml from "../components/EmailHtml";
import getEmailDate from "../util/getEmailDate";
import useEmailUser from "../util/useEmailUser";
import useGetEmailSenderName from "../util/useGetEmailSenderName";
import useIsCurrentUserEmail from "../util/useIsCurrentUserEmail";

import styles from "./ChildEmail.module.scss";
import ChildEmailBodyPreview from "./ChildEmailBodyPreview";
import EmailActivity from "./EmailActivity";
import EmailAttachmentsList from "./EmailAttachmentsList";
import EmailInfo from "./EmailInfo";
import EmailRecipientsParagraph from "./EmailRecipientsParagraph";
import EmailUser from "./EmailUser";
import messages from "./messages";

interface ChildEmailProps {
  email: Email;
  initialExpanded?: boolean;
  onReply: (repliedAll: boolean) => void;
}

const ChildEmail: React.FC<ChildEmailProps> = ({ email, initialExpanded, onReply }) => {
  const intl = useIntl();

  const emailUser = useEmailUser(email);
  const getEmailSenderName = useGetEmailSenderName();

  const containerRef = useRef<HTMLDivElement>(null);
  const [expanded, , , toggleExpanded] = useBoolean(initialExpanded);
  const [emailInfoVisible, showEmailInfo, hideEmailInfo] = useBoolean(false);

  const isCurrentUserEmail = useIsCurrentUserEmail();

  const replyAllAvailable = useMemo(() => {
    return (
      uniqBy(
        [email.from[0], ...email.recipientAddress, ...(email.cc ?? [])].filter(
          (emailRecipient) => !isCurrentUserEmail(emailRecipient.email)
        ),
        (emailRecipient) => emailRecipient.email
      ).length > 2
    );
  }, [email, isCurrentUserEmail]);

  const sender = useMemo(
    () => emailUser?.fullName ?? getEmailSenderName(email, false),
    [email, emailUser, getEmailSenderName]
  );

  const emailFormattedDate = useMemo(() => {
    const emailDate = getEmailDate(email);

    if (isToday(emailDate)) {
      return formatDate(emailDate, "p");
    }

    const formattedDate = formatDate(emailDate, intl.formatMessage(messages.dateFormatFull));
    const relativeTime = formatDistanceToNow(emailDate, injectLocale({ addSuffix: true }));

    return `${formattedDate} (${relativeTime})`;
  }, [intl, email]);

  useEffect(() => {
    if (!expanded) {
      hideEmailInfo();
    }
  }, [expanded, hideEmailInfo]);

  const handleReply = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onReply(false);
    },
    [onReply]
  );

  const handleReplyAll = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onReply(true);
    },
    [onReply]
  );

  return (
    <div className={cn({ [styles.expanded]: expanded }, styles.container)} ref={containerRef}>
      <div className={styles.header} onClick={toggleExpanded}>
        <EmailUser className={styles.headerUser} email={email} user={emailUser} />
        <div className={styles.headerContent}>
          <div className={styles.headerTitleContainer}>
            <div className={styles.headerTitle}>
              <Tooltip title={sender}>{sender}</Tooltip>
            </div>
            <div className={styles.headerDate}>{emailFormattedDate}</div>
          </div>
          {expanded && (
            <EmailRecipientsParagraph
              className={styles.emailRecipientsParagraph}
              email={email}
              onCollapse={hideEmailInfo}
              onExpand={showEmailInfo}
            />
          )}
          {expanded && emailInfoVisible && <EmailInfo className={styles.emailInfo} email={email} />}
          {!expanded && <ChildEmailBodyPreview className={styles.emailBodyPreview} email={email} />}
        </div>
        <div className={styles.headerInfo}>
          <EmailAttachmentsIcon email={email} />
          <EmailActivity email={email} />
        </div>
      </div>
      {expanded && (
        <div className={styles.content}>
          <div className={styles.actions}>
            <Tooltip title={intl.formatMessage(messages.replyTooltip)}>
              <Button
                className={styles.actionButton}
                icon={<FontAwesomeIcon icon={faReply} />}
                onClick={handleReply}
                type="text"
              />
            </Tooltip>
            {replyAllAvailable && (
              <Tooltip title={intl.formatMessage(messages.replyAllTooltip)}>
                <Button
                  className={styles.actionButton}
                  icon={<FontAwesomeIcon icon={faReplyAll} />}
                  onClick={handleReplyAll}
                  type="text"
                />
              </Tooltip>
            )}
          </div>
          <div className={styles.emailBody}>
            <EmailHtml email={email} showPreviousEmails />
            {email.attachments && (
              <EmailAttachmentsList attachments={email.attachments} messageId={email.messageId} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildEmail;
